import * as THREE from 'three';
import { AIRCRAFT, CONTROLS, ENV, GROUND } from '../core/SimConfig.js';
import { clamp, smoothstep } from '../utils.js';

const _invQuat = new THREE.Quaternion();
const _omegaBody = new THREE.Vector3();

// Reference dynamic pressure the flight model normalises control authority
// against (FlightModel.REF_Q). Surface commands are divided by the same ratio
// so a given stick asks for the same response at 60 m/s as at 200.
const REF_Q = 0.5 * ENV.airDensity * ENV.referenceAirspeed * ENV.referenceAirspeed;

/**
 * Stability layer between a manual stick and the control surfaces — the thing
 * that makes the raw airframe flyable from a keyboard or a centring stick.
 *
 * The bare flight model has no trim and its pitch authority isn't scheduled
 * with speed: centred elevator lets the nose fall away, and full elevator
 * stalls the jet below ~200 m/s and pulls ~11 G above it. The look-to-steer
 * autopilot hides that by closing the loop itself; every other scheme flies
 * through this instead.
 *
 * Pitch is a load-factor command. Centred stick asks for whatever holds the
 * current flight path — cos γ, bank-compensated so a turn holds altitude — and
 * full deflection asks for `nMax`/`nMin`. That demand is turned into a body
 * pitch rate (the rate at which the velocity vector has to swing to produce
 * it) and tracked by a PI loop; the integrator is the auto-trim. An AoA
 * limiter clips the rate demand approaching the stall. Both limits are soft
 * stops: the last stretch of travel past `overrideStart` opens them up, so a
 * deliberate full pull can over-G, or stall the jet when slow.
 *
 * Roll is a rate command that holds the bank when centred. Yaw passes
 * straight through.
 *
 * On the ground the pitch loop is bypassed (stick → elevator, so rotation is
 * the pilot's), the roll axis also steers the nosewheel, and the assist blends
 * in over `airborneBlend` once the wheels leave.
 *
 * Inputs are pilot-sense: pitch + = pull (nose up), roll + = right, yaw + =
 * nose right. Outputs are the aircraft's surface conventions (ailerons + =
 * roll left, rudder + = nose left).
 */
export default class FlightAssist {
  constructor() {
    this.out = { elevator: 0, ailerons: 0, rudder: 0 };
    this._pitchI = 0;
    this._bankHold = null; // captured bank (rad, + = right) while the roll stick is centred
    this._airborne = 0;    // 0..1 — direct (ground) → assisted pitch
  }

  /** Zero the trim and holds. `airborne` skips the post-lift-off blend. */
  reset(airborne = false) {
    this._pitchI = 0;
    this._bankHold = null;
    this._airborne = airborne ? 1 : 0;
    this.out.elevator = this.out.ailerons = this.out.rudder = 0;
  }

  /**
   * Bumpless hand-over: seed the trim integrator from the elevator something
   * else (the autopilot) is currently commanding, so taking an axis over
   * doesn't kick the nose.
   */
  sync(elevator) {
    this._pitchI = clamp(elevator, -1, 1);
    this._bankHold = null;
  }

  /**
   * @param {number} dt
   * @param {Aircraft} aircraft
   * @param {{pitch:number, roll:number, yaw:number}} stick — each in [-1, 1]
   * @param {number} [rollRateLimit] — fixed full-stick roll rate (rad/s),
   *   overriding the airframe-derived, speed-tapered one (Basic's takeover)
   */
  update(dt, aircraft, stick, rollRateLimit) {
    const cfg = CONTROLS.assist;
    const out = this.out;
    const fwd = aircraft.forwardVector;
    const right = aircraft.rightVector;
    const up = aircraft.upVector;
    const airspeed = aircraft.airspeed;

    _invQuat.copy(aircraft.quaternion).invert();
    _omegaBody.copy(aircraft.angularVelocity).applyQuaternion(_invQuat);
    const pitchRate = _omegaBody.x;   // + = nose up
    const rollRateR = -_omegaBody.z;  // + = rolling right

    // Surface effectiveness relative to the flight model's reference — floored
    // so the divide can't run away near zero airspeed.
    const qNorm = Math.max(Math.min(0.5 * ENV.airDensity * airspeed * airspeed / REF_Q, 1), 0.12);

    // ── Ground / airborne blend ──────────────────────────────────────────
    const onGround = aircraft.onGround;
    const blendRate = dt / Math.max(cfg.airborneBlend, 1e-3);
    this._airborne = onGround ? 0 : Math.min(1, this._airborne + blendRate);
    const w = smoothstep(this._airborne);

    // ── Pitch: load-factor command → pitch-rate PI ───────────────────────
    const V = Math.max(airspeed, 30);
    const vel = aircraft.velocity;
    const speed = vel.length();
    const cosGamma = speed > 1 ? Math.sqrt(Math.max(0, 1 - (vel.y / speed) ** 2)) : 1;
    // Bank compensation: holding the flight path in a bank of φ needs
    // n = cos γ / cos φ, which runs to infinity at knife-edge. c/(c² + k)·(1 + k)
    // matches it near wings-level, peaks around 2 G, and falls continuously
    // through zero at 90° — and goes negative inverted, so a centred stick
    // upside down pushes to hold the line instead of pulling into the ground.
    const bankR = Math.atan2(-right.y, up.y); // + = right wing down
    const c = Math.cos(bankR);
    const k = cfg.bankCompFloor;
    const n0 = cosGamma * (c / (c * c + k)) * (1 + k);
    const p = stick.pitch;
    // Past `overrideStart` the stick pushes through the protection: the G
    // ceiling and the AoA limit both open up toward full deflection.
    const over = smoothstep((Math.abs(p) - cfg.overrideStart) / (1 - cfg.overrideStart));
    const nTop = cfg.nMax + (cfg.nOverload - cfg.nMax) * (p > 0 ? over : 0);
    const nCmd = p >= 0 ? n0 + p * (nTop - n0) : n0 + p * (n0 - cfg.nMin);
    // Body pitch rate that swings the velocity vector hard enough to produce
    // nCmd: the normal acceleration beyond gravity's share along body-up.
    let qCmd = ENV.gravity * (nCmd - up.y) / V;

    // AoA limiter — clip the demand approaching the stall (both signs).
    const alpha = aircraft.alpha || 0;
    const alphaLim = (aircraft.stallAngle || 15 * Math.PI / 180)
      * (cfg.alphaLimitFrac + (cfg.alphaOverrideFrac - cfg.alphaLimitFrac) * over);
    qCmd = Math.min(qCmd, (alphaLim - alpha) * cfg.alphaGain);
    qCmd = Math.max(qCmd, (-alphaLim - alpha) * cfg.alphaGain);

    const err = qCmd - pitchRate;
    let elevator;
    if (w > 0) {
      this._pitchI = clamp(this._pitchI + (cfg.pitchKi * err * dt) / qNorm, -1, 1);
      const assisted = clamp(this._pitchI + (cfg.pitchKp * err) / qNorm, -1, 1);
      elevator = assisted * w + p * (1 - w);
    } else {
      // Wheels down: the stick is the elevator. Keep the integrator parked on
      // it so the hand-over at lift-off is bumpless.
      elevator = p;
      this._pitchI = p;
    }

    // ── Roll: rate command, bank hold when centred ───────────────────────
    // The airframe's steady roll rate per unit aileron, for the feed-forward
    // and the full-stick demand — read off the flight model's own numbers so a
    // change to roll authority doesn't need the assist re-tuning.
    const plantRate = AIRCRAFT.controlAuthority.roll * (aircraft.rollAuthorityScale ?? 1)
      / AIRCRAFT.dampingCoefficients.roll;
    const taper = clamp(airspeed / cfg.rollTaperSpeed, cfg.rollTaperFloor, 1);
    const rateMax = rollRateLimit ?? plantRate * cfg.rollRateFrac * taper;
    const r = stick.roll;
    let pCmd;
    if (Math.abs(r) > 0.02) {
      pCmd = r * rateMax;
      this._bankHold = null;
    } else if (Math.abs(fwd.y) > 0.9) {
      // Near vertical the bank angle is undefined — just stop the roll.
      pCmd = 0;
      this._bankHold = null;
    } else {
      if (this._bankHold === null) this._bankHold = bankR;
      let bankErr = this._bankHold - bankR;
      bankErr = Math.atan2(Math.sin(bankErr), Math.cos(bankErr));
      pCmd = clamp(bankErr * cfg.bankHoldGain, -rateMax, rateMax);
    }
    const aileronR = clamp(pCmd / plantRate + (cfg.rollKp * (pCmd - rollRateR)) / qNorm, -1, 1);

    // ── Ground handling ──────────────────────────────────────────────────
    // Below rotation speed the roll axis steers the nosewheel, and the
    // ailerons fade in with speed (they'd only bank the jet on its gear).
    const groundBlend = onGround
      ? 1 - smoothstep((airspeed - GROUND.vGround) / (GROUND.vRotate - GROUND.vGround))
      : 0;
    const yaw = clamp(stick.yaw + stick.roll * cfg.groundRollSteer * groundBlend, -1, 1);

    out.elevator = clamp(elevator, -1, 1);
    out.ailerons = onGround ? -r * (1 - groundBlend) : -aileronR;
    out.rudder = -yaw;
    return out;
  }
}
